import type { RunEtaPayload } from "@the-drop/types";

/**
 * Inline ETA + stops-ahead badge for the dispensary's active-run row. Internal-only surface —
 * unlike the customer-web ETA card, this one renders `stopsAhead` (the run's next stop count)
 * because `run.eta` is denylisted from customer namespaces (PR #374).
 *
 * **Three states** mirroring the customer-web card (`eta-card-body.tsx`), but with the internal
 * stops-ahead annotation:
 *   - **no data** (no `run.eta` received yet for this run): shows the API-fetched fallback
 *     (`fallbackEtaMinutes` — the legacy single-number "X mins" the row previously rendered)
 *   - **window**: "4:25 – 4:40 PM • next stop" (when stopsAhead = 0) OR "+2 stops ahead" (when > 0)
 *   - **running-late**: amber tinted window with the same stops-ahead annotation
 *
 * Pure render — no clock, deterministic on inputs.
 */
export type RunEtaInlineProps = {
  runEta: RunEtaPayload | undefined;
  /** Order whose ETA to surface from `runEta.stops[]`. When null/undefined → uses stops[0] (the next stop). */
  orderId?: string | null;
  /** Legacy fallback when no live `run.eta` has arrived yet — keeps the row populated mid-load. */
  fallbackEtaMinutes?: number | null;
};

export function RunEtaInline({ runEta, orderId, fallbackEtaMinutes }: RunEtaInlineProps) {
  const stop = runEta ? pickStop(runEta, orderId ?? null) : null;
  if (!stop) {
    return (
      <span className="text-sm text-[#333333]">
        {fallbackEtaMinutes != null && fallbackEtaMinutes > 0 ? `${fallbackEtaMinutes} mins` : "—"}
      </span>
    );
  }
  const windowLabel = `${formatEtaEdge(stop.etaStart)} – ${formatEtaEdge(stop.etaEnd)}`;
  const aheadLabel =
    stop.stopsAhead === 0 ? "next stop" : `+${stop.stopsAhead} ${stop.stopsAhead === 1 ? "stop" : "stops"} ahead`;
  const isRunningLate = isWindowInPast(stop.etaEnd);
  return (
    <span className="flex flex-col">
      <span
        className={`text-sm font-semibold ${
          isRunningLate ? "text-amber-700" : "text-[#111111]"
        }`}
      >
        {windowLabel}
      </span>
      <span className="text-2xs text-[#AAAAAA]">{aheadLabel}</span>
    </span>
  );
}

function pickStop(
  runEta: RunEtaPayload,
  preferredOrderId: string | null,
): RunEtaPayload["stops"][number] | null {
  if (preferredOrderId) {
    const match = runEta.stops.find((s) => s.orderId === preferredOrderId);
    if (match) return match;
  }
  // Default to stops_ahead = 0 (the next stop on the run).
  const next = runEta.stops.find((s) => s.stopsAhead === 0);
  if (next) return next;
  // Fallback to whatever the first entry is (defensive — shouldn't normally happen).
  return runEta.stops[0] ?? null;
}

function isWindowInPast(iso: string): boolean {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return t < Date.now();
}

/** h:mm A formatter — whole-minute precision per `docs/driver-eta.md`. */
function formatEtaEdge(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const mm = String(minutes).padStart(2, "0");
  return `${hours}:${mm} ${meridiem}`;
}
